<template>
  <div v-if="visible" class="confirm-dialog-overlay" @click="handleOverlayClick">
    <div class="confirm-dialog" @click.stop>
      <div class="dialog-header">
        <h3>{{ title }}</h3>
        <button class="close-btn" @click="cancel" aria-label="关闭">
          <span>×</span>
        </button>
      </div>
      
      <div class="dialog-content">
        <p>{{ message }}</p>
      </div>
      
      <div class="dialog-actions">
        <button
          v-if="cancelText"
          class="dialog-btn cancel-btn"
          @click="cancel"
          :style="{ order: confirmFirst ? 2 : 1 }"
        >
          {{ cancelText }}
        </button>
        <button
          v-if="confirmText"
          class="dialog-btn confirm-btn"
          @click="confirm"
          :style="{ order: confirmFirst ? 1 : 2 }"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

interface Props {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmFirst?: boolean
}

withDefaults(defineProps<Props>(), {
  title: '确认操作',
  confirmText: '确认',
  cancelText: '取消',
  confirmFirst: false
})

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const visible = ref(false)

// 键盘事件处理
const handleKeydown = (event: KeyboardEvent) => {
  if (!visible.value) return
  
  if (event.key === 'Escape') {
    cancel()
  } else if (event.key === 'Enter') {
    confirm()
  }
}

const confirm = () => {
  visible.value = false
  emit('confirm')
}

const cancel = () => {
  visible.value = false
  emit('cancel')
}

const handleOverlayClick = (event: Event) => {
  if (event.target === event.currentTarget) {
    cancel()
  }
}

// 显示对话框
const show = () => {
  visible.value = true
}

// 隐藏对话框
const hide = () => {
  visible.value = false
}

// 键盘事件监听
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// 暴露方法给父组件
defineExpose({
  show,
  hide
})
</script>

<style>
/* 基础对话框样式 - 默认主题 */
.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.confirm-dialog {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  animation: dialog-appear 0.3s ease-out;
}

@keyframes dialog-appear {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 16px;
}

.dialog-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.close-btn:hover {
  background: #f8f9fa;
  color: #495057;
}

.dialog-content {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.dialog-content p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  word-wrap: break-word;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
  flex-wrap: wrap;
}

.dialog-actions:has(> .dialog-btn:only-child) {
  justify-content: center;
}

.dialog-actions:has(> .dialog-btn:only-child) .dialog-btn {
  min-width: 200px;
  max-width: 300px;
}

.dialog-btn {
  flex: 1;
  min-width: 120px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-btn {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #e9ecef;
}

.cancel-btn:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.confirm-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
}

.confirm-btn:hover {
  background: linear-gradient(135deg, #5a6fd8, #6a4190);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.confirm-btn:active,
.cancel-btn:active {
  transform: translateY(0);
}

/* 移动端优化 */
@media (max-width: 768px) {
  .confirm-dialog-overlay {
    padding: 16px;
    align-items: flex-end;
  }
  
  .confirm-dialog {
    max-width: none;
    border-radius: 16px 16px 0 0;
    max-height: 80vh;
    animation: mobile-dialog-appear 0.3s ease-out;
  }
  
  @keyframes mobile-dialog-appear {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .dialog-header {
    padding: 20px 20px 0;
    padding-bottom: 16px;
  }
  
  .dialog-content {
    padding: 20px;
  }
  
  .dialog-actions {
    padding: 0 20px 20px;
    flex-direction: column-reverse;
  }
  
  .dialog-actions:has(> .dialog-btn:only-child) {
    flex-direction: column;
    justify-content: center;
  }
  
  .dialog-actions:has(> .dialog-btn:only-child) .dialog-btn {
    min-width: auto;
    max-width: none;
    width: 100%;
  }
  
  .dialog-btn {
    min-width: auto;
    width: 100%;
  }
  
  .dialog-header h3 {
    font-size: 1.1rem;
  }
  
  .dialog-content p {
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .confirm-dialog-overlay {
    padding: 12px;
  }
  
  .dialog-header {
    padding: 16px 16px 0;
    padding-bottom: 12px;
  }
  
  .dialog-content {
    padding: 16px;
  }
  
  .dialog-actions {
    padding: 0 16px 16px;
  }
  
  .dialog-btn {
    min-height: 44px;
    font-size: 0.95rem;
  }
}

/* 高对比度模式支持 */
@media (prefers-contrast: high) {
  .confirm-dialog {
    border: 2px solid #000;
  }
  
  .cancel-btn {
    border: 2px solid #000;
  }
}

/* 减少动画模式支持 */
@media (prefers-reduced-motion: reduce) {
  .confirm-dialog {
    animation: none;
  }
  
  .dialog-btn {
    transition: none;
  }
}
</style>